import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    List,
    ListItemText,
    CircularProgress,
    ListItemButton
} from '@mui/material';
import api from '../api';

interface RebarCartAssignmentDialogProps {
    open: boolean;
    onClose: () => void;
    onAssignSuccess: () => void;
    saleItemId: number;
}

interface RebarCart {
    id: number;
    upper_radius: number;
    lower_radius: number;
    width: number;
    height: number;
    company_name?: string;
    site_name?: string;
    item?: string;
}

const RebarCartAssignmentDialog: React.FC<RebarCartAssignmentDialogProps> = ({
    open,
    onClose,
    onAssignSuccess,
    saleItemId
}) => {
    const [materialType, setMaterialType] = useState<'new' | 'reuse'>('new');
    const [rebarCartData, setRebarCartData] = useState({
        upper_radius: '',
        lower_radius: '',
        width: '',
        height: ''
    });
    const [reuseRebarCarts, setReuseRebarCarts] = useState<RebarCart[]>([]);
    const [selectedReuseRebarCartId, setSelectedReuseRebarCartId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (materialType === 'reuse') {
            fetchReuseRebarCarts();
        }
    }, [materialType]);

    const fetchReuseRebarCarts = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/rebar-carts/available');
            setReuseRebarCarts(response.data);
        } catch (error) {
            console.error('재활용 철근대차 목록 조회 오류:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectRebarCart = (cart: RebarCart) => {
        setRebarCartData({
            upper_radius: cart.upper_radius.toString(),
            lower_radius: cart.lower_radius.toString(),
            width: cart.width.toString(),
            height: cart.height.toString()
        });
        setSelectedReuseRebarCartId(cart.id);
    };

    const handleAssign = async () => {
        try {
            if (materialType === 'new') {
                await api.post(`/api/sale-items/${saleItemId}/rebar-carts`, {
                    type: 'new',
                    upper_radius: Number(rebarCartData.upper_radius),
                    lower_radius: Number(rebarCartData.lower_radius),
                    width: Number(rebarCartData.width),
                    height: Number(rebarCartData.height),
                });
            } else if (materialType === 'reuse' && selectedReuseRebarCartId !== null) {
                await api.post(`/api/sale-items/${saleItemId}/rebar-carts`, {
                    type: 'reuse',
                    rebar_cart_id: selectedReuseRebarCartId,
                });
            }
            onAssignSuccess();
        } catch (error) {
            console.error('철근대차 자재 할당 오류:', error);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>철근대차 자재 할당</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal">
                    <InputLabel>자재 타입</InputLabel>
                    <Select
                        value={materialType}
                        onChange={(e) => {
                            setMaterialType(e.target.value as 'new' | 'reuse');
                            setSelectedReuseRebarCartId(null);
                            setRebarCartData({ upper_radius: '', lower_radius: '', width: '', height: '' });
                        }}
                        label="자재 타입"
                    >
                        <MenuItem value="new">신규</MenuItem>
                        <MenuItem value="reuse">재활용</MenuItem>
                    </Select>
                </FormControl>

                {(materialType === 'new' || selectedReuseRebarCartId !== null) && (
                    <>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="상부 R값"
                            type="number"
                            value={rebarCartData.upper_radius}
                            onChange={(e) => setRebarCartData({ ...rebarCartData, upper_radius: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="하부 R값"
                            type="number"
                            value={rebarCartData.lower_radius}
                            onChange={(e) => setRebarCartData({ ...rebarCartData, lower_radius: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="너비"
                            type="number"
                            value={rebarCartData.width}
                            onChange={(e) => setRebarCartData({ ...rebarCartData, width: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="높이"
                            type="number"
                            value={rebarCartData.height}
                            onChange={(e) => setRebarCartData({ ...rebarCartData, height: e.target.value })}
                        />
                    </>
                )}

                {materialType === 'reuse' && selectedReuseRebarCartId === null && (
                    <>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <List>
                                {reuseRebarCarts.map((cart) => (
                                    <ListItemButton key={cart.id} onClick={() => handleSelectRebarCart(cart)}>
                                        <ListItemText
                                            primary={`[${cart.company_name} / ${cart.site_name} / ${cart.item}] 상부 R: ${cart.upper_radius}, 하부 R: ${cart.lower_radius}, 너비: ${cart.width}, 높이: ${cart.height}`}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>취소</Button>
                <Button onClick={handleAssign} variant="contained" color="primary">
                    할당
                </Button>
            </DialogActions>
        </Dialog> 
    );
};

export default RebarCartAssignmentDialog;